import { AccountPermission } from "@syla/shared/types/models/AccountAccessBase";
import { getCurrentSubscription } from "@syla/shared/types/subscriptions/getCurrentSubscription";
import { getSubscriptionState } from "@syla/shared/types/subscriptions/getSubscriptionState";
import {
  Lifecycle,
  SubscriptionType,
} from "@syla/shared/types/subscriptions/subscriptionState";
import { Account } from "../types/Account";
import { Subscription } from "../types/plans/subscription";

export type AccountWithPermissions = Pick<Account, "permissions">;

export function hasAccountPermission(
  account: AccountWithPermissions | undefined,
  permission: AccountPermission,
) {
  return (
    (account && account.permissions.some((p) => p === permission)) || false
  );
}

/**
 * Has access to manage an existing subscription, including Upgrade, Downgrade,
 * Cancel, and Resume.
 */
export function canManageExistingSubscription({
  subscriptions,
}: {
  subscriptions: Subscription[] | undefined;
}) {
  const subscription = getCurrentSubscription(subscriptions);

  if (!subscription) {
    return false;
  }

  // for a manual subscription, there is no billing owner, so anyone is allowed
  // to manage it.
  const state = getSubscriptionState(subscription);
  if (state.type === SubscriptionType.Manual) {
    return true;
  }

  if (subscription.billingAccountAccess) {
    return true;
  }

  return false;
}

/**
 * Has access to purchase a new subscription.
 */
export function canPurchaseSubscription({
  account,
  subscriptions,
}: {
  account: AccountWithPermissions | undefined;
  subscriptions: Subscription[] | undefined;
}) {
  if (!account) {
    return false;
  }

  if (!hasAccountPermission(account, AccountPermission.PurchaseSubscription)) {
    return false;
  }

  const subscription = getCurrentSubscription(subscriptions);

  if (!subscription) {
    return true;
  }

  if (
    subscription &&
    getSubscriptionState(subscription).lifecycle === Lifecycle.Terminated
  ) {
    return true;
  }

  return false;
}
